(ns frontend.view.settings2.connected-plugins.view
  "Connected Add-ins settings view - allows users to manage add-in connections.
   This component is embedded in the user's personal profile Security tab."
  (:require
    [clojure.string :as string]
    [common.connection-lifetime :as connection-lifetime]
    [common.localization :as loc]
    [frontend.state.current-session :as session]
    [frontend.view.components.clipboard :as clipboard]
    [frontend.view.modals.confirmation.view :as confirmation-modal]
    [frontend.view.modals.events :as modals]
    [frontend.view.modals.hidden-emails.view :as hidden-emails-modal]
    [frontend.view.settings2.connected-plugins.events :as events]
    [frontend.view.settings2.connected-plugins.sub :as sub]
    [re-frame.core :as rf]
    [reagent.core :as r]
    [ui.components.button.view :as button]
    [ui.components.radix-ui.tooltip.view :as tooltip]
    [ui.dom-globals :as dg]
    [ui.editors.labelled-input.view :as labelled-input]
    [ui.state.session.sub :as session-sub]
    [ui.util :as ui-util]))

;;
;; Helper Functions
;;

(defn- dot-separator []
  [:i.fas.fa-circle {:class "text-[4px] text-base-light-text-paler mx-0.5"}])

(defn time-remaining
  "Calculate time remaining until expiry in seconds"
  [expires-ts]
  (max 0 (quot (- expires-ts (js/Date.now)) 1000)))

(def ^:private plugin-type->label
  {:plugin.type/outlook "Outlook"
   :plugin.type/mcp     "MCP"})

(defn plugin-type-label
  "Get human-readable label for plugin type"
  [plugin-type]
  (get plugin-type->label plugin-type (str plugin-type)))

(def ^:private addin-types
  [{:plugin-type     :plugin.type/outlook
    :label           "Outlook"
    :icon-class      "fab fa-microsoft"
    :description-key :settings/outlook-addin-description
    :benefits-key    :settings/outlook-addin-benefits
    :modules         [:module/outlook-addin]}
   {:plugin-type     :plugin.type/mcp
    :label           "MCP"
    :icon-class      "fal fa-robot"
    :description-key :settings/mcp-connector-description
    :benefits-key    :settings/mcp-connector-benefits
    :modules         [:module/mcp-connector-read :module/mcp-connector-write]}])

;;
;; Components
;;

(defn loading
  []
  [:div
   {:class "text-center py-4"}
   [:i.fal.fa-spinner.fa-pulse.text-base-light-text-paler]])

(defn error-view
  [error-message]
  [:div
   {:class "py-4 text-center text-danger-regular text-sm"}
   [:i.fal.fa-exclamation-circle.mr-2]
   [:span error-message]])

(defn countdown-timer
  "A countdown timer component that updates every second.
   Uses local reagent atom since it's component-scoped interval state."
  [expires-ts on-expired]
  (let [remaining (r/atom (time-remaining expires-ts))
        interval-id (r/atom nil)]
    (r/create-class
      {:component-did-mount
       (fn [_]
         (reset! interval-id
                 (js/setInterval
                   (fn []
                     (let [new-remaining (time-remaining expires-ts)]
                       (reset! remaining new-remaining)
                       (when (<= new-remaining 0)
                         (when @interval-id
                           (js/clearInterval @interval-id))
                         (on-expired))))
                   1000)))
       :component-will-unmount
       (fn [_]
         (when @interval-id
           (js/clearInterval @interval-id)))
       :reagent-render
       (fn [_expires-ts _on-expired]
         (let [mins (quot @remaining 60)
               secs (mod @remaining 60)]
           [:span
            {:class (str "font-mono text-sm "
                         (if (< @remaining 60) "text-danger-regular" "text-base-light-text-paler"))}
            (str mins ":" (when (< secs 10) "0") secs)]))})))

(defn- pairing-code-generator
  "Component for generating pairing codes for a specific plugin type"
  [plugin-type]
  (let [generating? @(rf/subscribe [::sub/generating?])
        pairing-code @(rf/subscribe [::sub/pairing-code plugin-type])
        pairing-code-expires @(rf/subscribe [::sub/pairing-code-expires plugin-type])
        pairing-error @(rf/subscribe [::sub/pairing-error plugin-type])
        connection-name @(rf/subscribe [::sub/connection-name])
        connection-lifetime @(rf/subscribe [::sub/connection-lifetime])
        addin-tenant-max-lifetime @(rf/subscribe [::sub/tenant-max-lifetime])
        mcp-tenant-max-lifetime @(rf/subscribe [::sub/tenant-mcp-max-lifetime])
        read-only? @(rf/subscribe [::sub/read-only?])
        tenant @(rf/subscribe [::session-sub/tenant])
        polling? @(rf/subscribe [::sub/polling?])
        connection-established? @(rf/subscribe [::sub/connection-established?])
        mcp? (= plugin-type :plugin.type/mcp)
        mcp-allow-write? (:tenant/mcp-allow-write? tenant)
        tenant-max-lifetime (if mcp?
                              mcp-tenant-max-lifetime
                              addin-tenant-max-lifetime)
        lifetime-options (->> (connection-lifetime/filter-options-by-max tenant-max-lifetime)
                              (map (fn [{:keys [value key]}]
                                     {:value value :label (loc/tr key)})))]
    [:div
     {:class "flex flex-col gap-3"}
     [:div
      {:class "font-bold text-base"}
      (loc/tr :settings/generate-pairing-code)]

     (cond
       ;; Connection was just established - show success message
       connection-established?
       [:div
        {:class "p-3 border border-solid border-base-light-border-paler rounded flex items-center gap-3"}
        [:i.fal.fa-check-circle.text-success-regular.text-lg]
        [:div
         {:class "flex flex-col gap-1"}
         [:span {:class "font-semibold text-sm text-success-regular"}
          (loc/tr :settings/connection-success)]
         [:span {:class "text-xs text-base-light-text-paler"}
          (loc/tr :settings/connection-success-description)]]]

       ;; Show generated code
       pairing-code
       [:div
        {:class "p-3 border border-solid border-base-light-border-paler rounded flex flex-col gap-3"}
        [:div
         {:class "flex items-center justify-between"}
         [:div {:class "flex items-center gap-2"}
          [:span
           {:class "text-sm text-base-light-text-paler m-0"}
           (loc/tr :settings/your-pairing-code)]
          ;; Polling indicator
          (when polling?
            [:span
             {:class "flex items-center gap-1 text-xs text-primary-regular"}
             [:i.fal.fa-sync.fa-spin]
             (loc/tr :settings/waiting-for-connection)])]
         [countdown-timer
          pairing-code-expires
          #(rf/dispatch [::events/clear-pairing-code plugin-type])]]
        [:div
         {:class "flex items-center gap-2"}
         [:code
          {:class "text-md font-mono font-semibold text-base-light-text tracking-wide px-3 py-1.5 bg-base-regular rounded"}
          pairing-code]
         [button/sm-secondary-hollow
          {:on-click (fn []
                       (clipboard/copy-to pairing-code))}
          [:span
           [:i.fal.fa-copy.mr-1]
           (loc/tr :copy)]]
         [button/sm-secondary-hollow
          {:on-click #(rf/dispatch [::events/clear-pairing-code plugin-type])}
          (loc/tr :cancel)]]]

       ;; Show input form
       :else
       [:div
        {:class "flex flex-col gap-3"}
        [:div
         {:class "max-w-xs"}
         [labelled-input/view
          {:label       (loc/tr :settings/connection-name)
           :data-type   :data.type/text-simple
           :value       (or connection-name "")
           :placeholder (loc/tr :settings/connection-name-placeholder)
           :on-change   #(rf/dispatch [::events/set-connection-name %])}]]

        ;; Connection lifetime dropdown
        (when (seq lifetime-options)
          [:div
           {:class "max-w-xs"}
           [:div
            {:class "flex items-center gap-1 mb-1"}
            [:label
             {:class "text-sm font-semibold m-0"}
             (loc/tr :settings/connection-lifetime)]
            [tooltip/view
             {}
             [:i.fal.fa-info-circle.text-base-light-text-paler.cursor-help.text-sm.m-0]
             [:div {:class "max-w-sm text-sm whitespace-pre-line"}
              (loc/tr :settings/connection-lifetime-tooltip)]]]
           [labelled-input/view
            {:data-type :data.type/keyword
             :value     connection-lifetime
             :options   lifetime-options
             :on-change #(rf/dispatch [::events/set-connection-lifetime %])}]])

        ;; Read-only checkbox — MCP only
        ;; When tenant write is disabled: always checked and disabled (forced read-only)
        ;; When tenant write is enabled: user can toggle freely
        (when mcp?
          (let [forced-read-only? (not mcp-allow-write?)]
            [:label
             {:class (str "flex items-center gap-2 select-none text-sm "
                          (if forced-read-only? "cursor-not-allowed opacity-60" "cursor-pointer"))}
             [:input
              {:type      "checkbox"
               :checked   (or forced-read-only? read-only?)
               :disabled  forced-read-only?
               :on-change (when-not forced-read-only?
                            (fn [event]
                              (rf/dispatch [::events/set-read-only?
                                            (dg/event-target-checked? event)])))}]
             [:span (loc/tr :settings/mcp-read-only-label)]
             [tooltip/view
              {}
              [:i.fal.fa-info-circle.text-base-light-text-paler.cursor-help.text-sm]
              [:div {:class "max-w-sm text-sm"}
               (if forced-read-only?
                 (loc/tr :settings/mcp-read-only-forced-tooltip)
                 (loc/tr :settings/mcp-read-only-tooltip))]]]))

        (when pairing-error
          [:div
           {:class "text-danger-regular text-sm"}
           pairing-error])

        (let [connection-name-empty? (empty? (string/trim (or connection-name "")))]
          [:div
           [tooltip/view
            {:disabled? (not connection-name-empty?)}
            [button/sm-primary-solid
             {:on-click  (fn []
                           (rf/dispatch [::events/generate-pairing-code
                                         connection-name
                                         plugin-type
                                         connection-lifetime
                                         read-only?]))
              :disabled? (or generating? connection-name-empty?)}
             (if generating?
               [:span
                [:i.fal.fa-spinner.fa-spin.mr-1]
                (loc/tr :generating)]
               [:span
                [:i.fal.fa-key.mr-1]
                (loc/tr :settings/generate-code)])]
            [:span (loc/tr :settings/connection-name-required)]]])])]))

(defn- open-revoke-confirmation!
  "Open confirmation modal before revoking a connection"
  [connection-id connection-name]
  (modals/open-modal!
    confirmation-modal/modal-key
    {:title          (loc/tr :settings/revoke-connection-title)
     :body           [:div
                      [:span (loc/tr :settings/revoke-connection-confirmation)]
                      [:br]
                      [:b (or connection-name (loc/tr :settings/unknown-connection))]]
     :confirm-text   (loc/tr :revoke)
     :skip-checkbox? true
     :on-confirm     #(rf/dispatch [::events/revoke-connection connection-id])}))

(defn connection-row
  "A single connected connection row"
  [connection]
  (let [{:keys [db/id token/plugin-connection-name token/plugin-type token/last-used-at
                common/created-on token/hard-expires token/connection-lifetime-kw]} connection
        revoking? @(rf/subscribe [::sub/revoking?])
        tenant @(rf/subscribe [::session-sub/tenant])
        format-ts #(ui-util/display-date-timestamp tenant %)
        unlimited? (= connection-lifetime-kw :connection-lifetime/unlimited)]
    [:div
     {:class "px-3 py-2 flex items-center justify-between border border-solid border-base-light-border-paler rounded"}
     [:div
      {:class "flex flex-col gap-0.5"}
      [:div
       {:class "font-semibold text-sm"}
       (or plugin-connection-name (loc/tr :settings/unknown-connection))]
      [:div
       {:class "flex items-center gap-1.5 text-xs text-base-light-text-paler"}
       [:span (plugin-type-label plugin-type)]
       [dot-separator]
       [:span (str (loc/tr :settings/connected-on) " " (format-ts created-on))]
       (when last-used-at
         [:<>
          [dot-separator]
          [:span (str (loc/tr :settings/last-used) " " (format-ts last-used-at))]])
       (if unlimited?
         [:<>
          [dot-separator]
          [:span (loc/tr :settings/expires-never)]]
         (when hard-expires
           [:<>
            [dot-separator]
            [:span (str (loc/tr :settings/expires-on) " " (format-ts hard-expires))]]))]]
     [:div
      [button/sm-danger-solid
       {:on-click #(open-revoke-confirmation! id plugin-connection-name)
        :disabled revoking?}
       (if revoking?
         [:i.fal.fa-spinner.fa-spin]
         (loc/tr :revoke))]]]))

(defn- connections-section
  "List of connections for a specific plugin type"
  [plugin-type]
  (let [connections @(rf/subscribe [::sub/connections-for-plugin-type plugin-type])
        revoke-error @(rf/subscribe [::sub/revoke-error])]
    [:div
     {:class "flex flex-col gap-3"}
     [:div
      {:class "font-bold text-base"}
      (loc/tr :settings/active-connections)]

     (when revoke-error
       [:div
        {:class "text-danger-regular text-sm"}
        revoke-error])

     (if (empty? connections)
       [:div
        {:class "p-3 border border-solid border-base-light-border-paler rounded text-center text-base-light-text-paler"}
        [:i.fal.fa-plug.mr-2]
        [:span {:class "text-sm"} (loc/tr :settings/no-active-connections)]]

       [:div
        {:class "flex flex-col gap-2"}
        (for [connection connections]
          ^{:key (:db/id connection)}
          [connection-row connection])])]))

(defn- mcp-connector-url
  "The URL to add Zapflow under in an MCP client. For a tenant with a custom
   login page it is tenant-specific, so signing in lands on that page."
  []
  (when-let [mcp-url @(rf/subscribe [::sub/mcp-url])]
    [:div
     {:class "flex flex-col gap-2"}
     [:div
      {:class "font-bold text-base"}
      (loc/tr :settings/mcp-connector-url)]
     [:div
      {:class "flex items-center gap-2"}
      [:code
       {:class "text-sm font-mono text-base-light-text px-3 py-1.5 bg-base-regular rounded break-all"}
       mcp-url]
      [button/sm-secondary-hollow
       {:on-click (fn []
                    (clipboard/copy-to mcp-url))}
       [:span
        [:i.fal.fa-copy.mr-1]
        (loc/tr :copy)]]]
     [:span
      {:class "text-xs text-base-light-text-paler"}
      (loc/tr :settings/mcp-connector-url-description)]]))

(defn- addin-card
  "Card component for a single add-in type"
  [{:keys [plugin-type label icon-class description-key benefits-key]}]
  [:div
   {:class "p-3 border border-solid border-base-light-border-paler rounded flex flex-col gap-4"}
   [:div {:class "flex items-center gap-2 text-base-light-text font-bold"}
    [:i {:class icon-class}]
    [:span (str label " Add-in")]]
   [:p {:class "text-sm text-base-light-text-paler m-0"}
    (loc/tr description-key)]
   [:p {:class "text-sm text-base-light-text-paler m-0"}
    (loc/tr benefits-key)]
   (when (= plugin-type :plugin.type/mcp)
     [mcp-connector-url])
   [pairing-code-generator plugin-type]
   [connections-section plugin-type]])

(defn hidden-emails-link
  "Link to open hidden emails modal with count.
   Only shown when there are hidden emails."
  []
  (let [hidden-emails @(rf/subscribe [::sub/hidden-emails])
        count (count hidden-emails)]
    (when (pos? count)
      [:div
       {:class "flex flex-col gap-3"}
       [:div
        {:class "font-bold text-[18px]"}
        (loc/tr :settings/hidden-emails)]
       [:div
        {:class "flex items-center gap-2"}
        [:span
         {:class "text-sm text-base-light-text-paler"}
         (loc/tr :settings/hidden-emails-count [count])]
        [:a
         {:class    "text-sm text-primary-regular cursor-pointer hover:underline"
          :on-click #(modals/open-modal! hidden-emails-modal/modal-key {})}
         (loc/tr :settings/view-hidden-emails)]]])))

(defn content
  []
  (r/create-class
    {:component-did-mount
     (fn [_]
       (rf/dispatch [::events/init]))
     :component-will-unmount
     (fn [_]
       (rf/dispatch [::events/cleanup]))
     :reagent-render
     (fn []
       (let [loading-state @(rf/subscribe [::sub/loading-state])
             connection-name-loaded? @(rf/subscribe [::sub/connection-name-loaded?])
             error @(rf/subscribe [::sub/error])]
         (cond
           (= loading-state :error)
           [error-view error]

           (or (= loading-state :loading)
               (not connection-name-loaded?))
           [loading]

           :else
           [:div
            {:class "flex flex-col gap-6"}
            (->> addin-types
                 (keep (fn [{:keys [plugin-type modules] :as addin}]
                         (when (some (fn [module]
                                       (session/has-module-access? @session/account module)) modules)
                           ^{:key plugin-type}
                           [addin-card addin])))
                 (doall))
            [hidden-emails-link]])))}))
